import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext"; // Import useAuth

// Removed UserData interface

interface Lobby {
  roomId: string;
  name: string;
  clients: number;
  maxClients: number;
  hasPassword: boolean;
  metadata?: {
    type?: string;
    gameMode?: string;
  };
}

const SERVER_URL = process.env.REACT_APP_SERVER_HOST;
const CLIENT_URL = process.env.REACT_APP_CLIENT_HOST;

// Remove props interface LobbiesProps

const Lobbies: React.FC = () => {
  // No props needed now
  const { user } = useAuth(); // Get user from context
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newLobbyName, setNewLobbyName] = useState("");
  const [newLobbyPassword, setNewLobbyPassword] = useState("");
  const [newLobbyType, setNewLobbyType] = useState("vorld");
  const [newLobbyRSPMode, setNewLobbyRSPMode] = useState("endless");
  const [error, setError] = useState<string | null>(null);

  // Derive isLoggedIn from the user context
  const isLoggedIn = !!user;

  // Function to get display name for lobby type
  const getLobbyTypeDisplayName = (typeCode?: string): string => {
    switch (typeCode) {
      case "rsp":
        return "Rock Paper Scissors";
      case "vorld":
      default:
        return "Vorld"; // Default to Vorld
    }
  };

  const fetchLobbies = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/lobby/list`);
      if (!response.ok) throw new Error("Failed to fetch lobbies");
      const data = await response.json();
      setLobbies(data);
    } catch (error) {
      setError("Failed to load lobbies");
      console.error("Error fetching lobbies:", error);
    }
  };

  useEffect(() => {
    fetchLobbies();
    // Refresh lobby list every 5 seconds
    const interval = setInterval(fetchLobbies, 5000);
    return () => clearInterval(interval);
  }, []);

  const createLobby = async () => {
    try {
      // Construct metadata including gameMode if RSP is selected
      const metadata: { [key: string]: any } = {};
      if (newLobbyType === "rsp") {
        metadata.gameMode = newLobbyRSPMode;
      }

      const response = await fetch(`${SERVER_URL}/lobby/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCookie("auth_token")}`,
        },
        body: JSON.stringify({
          name: newLobbyName,
          password: newLobbyPassword || null,
          type: newLobbyType,
          metadata: metadata,
        }),
      });

      if (!response.ok) throw new Error("Failed to create lobby");
      const reservation = await response.json();
      setIsCreating(false);
      setNewLobbyName("");
      setNewLobbyPassword("");
      setNewLobbyRSPMode("endless");

      // Redirect to game client based on type
      const gamePath = newLobbyType === "rsp" ? "/rsp" : "/vorld";
      const encodedReservation = encodeURIComponent(
        JSON.stringify(reservation)
      );

      // Construct final redirect URL (only reservation)
      const redirectUrl = `${CLIENT_URL}${gamePath}?reservation=${encodedReservation}`;
      console.log("Redirecting creator to (secure):", redirectUrl);
      window.location.href = redirectUrl;
    } catch (error) {
      setError("Failed to create lobby");
      console.error("Error creating lobby:", error);
    }
  };

  const joinLobby = async (lobby: Lobby) => {
    try {
      let password = lobby.hasPassword ? prompt("Enter lobby password:") : null;

      if (lobby.hasPassword && password === null) {
        console.log("Password prompt cancelled.");
        return;
      }

      const response = await fetch(`${SERVER_URL}/lobby/join/${lobby.roomId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCookie("auth_token")}`,
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError("Incorrect password");
          return;
        }
        let errorMsg = "Failed to join lobby";
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          /* Ignore if response is not JSON */
        }
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      const reservation = await response.json();

      const roomData = lobbies.find((l) => l.roomId === lobby.roomId);
      if (!roomData)
        throw new Error("Failed to get lobby data for redirect path");

      const gameType = roomData.metadata?.type || "vorld";
      const gamePath = gameType === "rsp" ? "/rsp" : "/vorld";

      const encodedReservation = encodeURIComponent(
        JSON.stringify(reservation)
      );

      window.location.href = `${CLIENT_URL}${gamePath}?reservation=${encodedReservation}`;
    } catch (error) {
      console.error("Error joining lobby:", error);
      if (!error) {
        setError("An unexpected error occurred while joining the lobby.");
      }
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <span
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <svg
              className="fill-current h-6 w-6 text-red-500"
              role="button"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </span>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          Available Lobbies
        </h2>
        {lobbies.length > 0 ? (
          lobbies.map((lobby) => (
            <div
              key={lobby.roomId}
              className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center space-x-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {lobby.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Type:{" "}
                    <span className="font-medium text-gray-600">
                      {getLobbyTypeDisplayName(lobby.metadata?.type)}
                    </span>
                  </p>
                  <p className="text-sm text-gray-500">
                    Players: {lobby.clients}/{lobby.maxClients}
                  </p>
                </div>
                {lobby.hasPassword && (
                  <span className="text-yellow-500" title="Password Protected">
                    🔒
                  </span>
                )}
              </div>
              <button
                onClick={() => joinLobby(lobby)}
                disabled={lobby.clients >= lobby.maxClients || !isLoggedIn}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors
                  ${
                    lobby.clients >= lobby.maxClients
                      ? "bg-gray-400 cursor-not-allowed"
                      : !isLoggedIn
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
              >
                {lobby.clients >= lobby.maxClients ? "Full" : "Join"}
              </button>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-4">
            No lobbies available right now.
          </p>
        )}
      </div>

      {!isCreating ? (
        isLoggedIn && user ? (
          <button
            onClick={() => {
              setNewLobbyName(`${user.nick}'s Lobby`);
              setNewLobbyType("vorld");
              setNewLobbyRSPMode("endless");
              setIsCreating(true);
              setError(null);
            }}
            className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Create New Lobby
          </button>
        ) : (
          <button
            disabled
            className="w-full bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed"
            title="You must be logged in to create a lobby"
          >
            Login to Create Lobby
          </button>
        )
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-sm space-y-4 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Create Lobby
          </h3>
          <input
            type="text"
            placeholder="Lobby Name"
            value={newLobbyName}
            onChange={(e) => setNewLobbyName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <input
            type="password"
            placeholder="Password (optional)"
            value={newLobbyPassword}
            onChange={(e) => setNewLobbyPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div>
            <label
              htmlFor="lobbyType"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Game Type:
            </label>
            <select
              id="lobbyType"
              value={newLobbyType}
              onChange={(e) => setNewLobbyType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="vorld">Vorld</option>
              <option value="rsp">Rock Paper Scissors</option>
            </select>
          </div>

          {newLobbyType === "rsp" && (
            <div>
              <label
                htmlFor="rspMode"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RSP Mode:
              </label>
              <select
                id="rspMode"
                value={newLobbyRSPMode}
                onChange={(e) => setNewLobbyRSPMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
              >
                <option value="endless">Endless</option>
                <option value="bestOf3">Best of 3 (First to 3 wins)</option>
              </select>
            </div>
          )}

          <div className="flex space-x-2 justify-end">
            <button
              onClick={() => {
                setIsCreating(false);
                setError(null);
              }}
              className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={createLobby}
              disabled={!newLobbyName}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                !newLobbyName
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600"
              }`}
            >
              Create & Join
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get cookie (needed for Auth Bearer token)
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

export default Lobbies;
